"use client";

import {
  createContext,
  useCallback,
  use,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface ListingQuoteSummary {
  totalCents: number;
  quantity: number;
}

export interface ListingSelection {
  quantity: number;
  goodsCents: number;
}

interface ListingQuoteSummaryContextValue {
  summary: ListingQuoteSummary | null;
  setSummary: (summary: ListingQuoteSummary | null) => void;
  selection: ListingSelection | null;
  setSelection: (selection: ListingSelection | null) => void;
  canBuy: boolean;
  setCanBuy: (canBuy: boolean) => void;
  actionLabel: string;
  setActionLabel: (label: string) => void;
  buyRef: React.RefObject<(() => void) | null>;
}

const DEFAULT_ACTION_LABEL = "Buy now";

const ListingQuoteSummaryContext =
  createContext<ListingQuoteSummaryContextValue>({
    summary: null,
    setSummary: () => {},
    selection: null,
    setSelection: () => {},
    canBuy: false,
    setCanBuy: () => {},
    actionLabel: DEFAULT_ACTION_LABEL,
    setActionLabel: () => {},
    buyRef: { current: null },
  });

export function ListingQuoteSummaryProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [summary, setSummary] = useState<ListingQuoteSummary | null>(null);
  const [selection, setSelection] = useState<ListingSelection | null>(null);
  const [canBuy, setCanBuy] = useState(false);
  const [actionLabel, setActionLabel] = useState(DEFAULT_ACTION_LABEL);
  const buyRef = useRef<(() => void) | null>(null);
  const value = useMemo(
    () => ({
      summary,
      setSummary,
      selection,
      setSelection,
      canBuy,
      setCanBuy,
      actionLabel,
      setActionLabel,
      buyRef,
    }),
    [summary, selection, canBuy, actionLabel],
  );
  return (
    <ListingQuoteSummaryContext value={value}>
      {children}
    </ListingQuoteSummaryContext>
  );
}

export function useListingQuoteSummary() {
  const { summary, selection, canBuy, actionLabel, buyRef } = use(
    ListingQuoteSummaryContext,
  );
  const buy = useCallback(() => buyRef.current?.(), [buyRef]);
  return { summary, selection, canBuy, actionLabel, buy };
}

export function usePublishListingQuote({
  totalCents,
  goodsCents,
  quantity,
  actionLabel,
  buy,
}: {
  totalCents: number | null;
  goodsCents: number;
  quantity: number;
  actionLabel: string;
  buy: () => void;
}) {
  const { setSummary, setSelection, setCanBuy, setActionLabel, buyRef } = use(
    ListingQuoteSummaryContext,
  );

  useEffect(() => {
    buyRef.current = buy;
  });

  useEffect(() => {
    setCanBuy(true);
    return () => {
      buyRef.current = null;
      setCanBuy(false);
      setSummary(null);
      setSelection(null);
      setActionLabel(DEFAULT_ACTION_LABEL);
    };
  }, [buyRef, setCanBuy, setSummary, setSelection, setActionLabel]);

  useEffect(() => {
    setActionLabel(actionLabel);
  }, [setActionLabel, actionLabel]);

  useEffect(() => {
    setSummary(totalCents === null ? null : { totalCents, quantity });
  }, [setSummary, totalCents, quantity]);

  useEffect(() => {
    setSelection({ quantity, goodsCents });
  }, [setSelection, goodsCents, quantity]);
}
